import { useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { getCourseCatalogsDetails } from '../../../services/dataQcDetailsService';
import SelectDropdown from '../../reusable/SelectDropdown';
import { AcademicPeriod, CourseCatalogDetails, SubjectArea } from '../../../interfaces/DataQcInterface';
import WithDataWrapper from '../../reusable/WithDataWrapper';
import CustomDivider from '../../reusable/CustomDivider';
import DataQcCardInfoGrid from '../cte courses/DataQcCardInfoGrid';

import './CourseCatalogSideDrawerContent.css';
import useSchools from '../../hooks/useSchools';

type GridItemsLabelsKeys = 'is_college_preparatory_count' | 'honors_count' | 'cte_count';

const gridItemsLabels: { [key in GridItemsLabelsKeys]: string } = {
  // general_count: 'General courses',
  is_college_preparatory_count: 'College Preparatory courses',
  honors_count: 'Honors courses',
  cte_count: 'CTE courses',
};

interface CourseCatalogSideDrawerContentProps {
  data: CourseCatalogDetails;
}

export function CourseCatalogSideDrawerContent({ data }: CourseCatalogSideDrawerContentProps) {
  const { academic_periods } = data;

  const initialState = academic_periods.length > 0 ? academic_periods[academic_periods.length - 1].period : '';
  const [selectedPeriod, setSelectedPeriod] = useState(initialState);

  const filterData = () => {
    if (!selectedPeriod || selectedPeriod.length === 0) return academic_periods;

    return academic_periods.filter((academicPeriod) => academicPeriod.period === selectedPeriod);
  };

  const generateItems = (subjectArea: SubjectArea) => {
    let items = (Object.keys(gridItemsLabels) as GridItemsLabelsKeys[]).map((key) => ({
      label: gridItemsLabels[key],
      value: subjectArea[key],
    }));

    return items;
  };

  const renderData = (filteredData: AcademicPeriod[]) => {
    return filteredData.map((academicPeriod, index) => {
      const periodCourseCount = academicPeriod.subject_areas.reduce((sum, sa) => sum + sa.course_count, 0);

      return (
        <div key={index} className="course-catalogs-details" data-testid="course-catalogs-details">
          <div className="header">
            <p>
              <span>{periodCourseCount}</span>courses
            </p>
            <p>
              <span>{academicPeriod.subject_areas.length}</span>subject areas
            </p>
          </div>
          {academicPeriod.subject_areas.map((subjectArea, index) => (
            <div key={index} data-testid="subject-area-list">
              <CustomDivider orientation="horizontal" className="light" />
              <p className="subject">
                {subjectArea.name}: {subjectArea.course_count} courses
              </p>
              <div className="courses">
                <DataQcCardInfoGrid items={generateItems(subjectArea)} />
              </div>
            </div>
          ))}
        </div>
      );
    });
  };

  const handleChange = (event: SelectChangeEvent) => {
    setSelectedPeriod(event.target.value);
  };

  return (
    <>
      {initialState ? (
        <div>
          <SelectDropdown
            label="Year"
            selectedValue={selectedPeriod}
            handleChange={handleChange}
            handleDelete={() => setSelectedPeriod('')}
            items={data.academic_periods.map((academicPeriod) => {
              return { label: academicPeriod.period, value: academicPeriod.period };
            })}
            formControlClasses={['width-40']}
            fullWidth={false}
          />
          {renderData(filterData())}
        </div>
      ) : (
        <p>No data available!</p>
      )}
    </>
  );
}

const CourseCatalogSideDrawerContentWithData = () => {
  const { dataQcSchool } = useSchools();

  let fetchData = (eoaId: string, access_token: string) => getCourseCatalogsDetails(eoaId, access_token, dataQcSchool);

  return (
    <WithDataWrapper
      fetchData={fetchData}
      errorMessage="Error occurred while fetching course catalog details data!"
      initialState={{ academic_periods: [] }}
      options={{
        dependencies: [dataQcSchool],
        areDependenciesRequired: false,
      }}
    >
      {(data) => <CourseCatalogSideDrawerContent data={data} />}
    </WithDataWrapper>
  );
};

export default CourseCatalogSideDrawerContentWithData;
